import React, { useState, useEffect, useCallback, useRef } from "react";
import { Routes, Route, Navigate, useNavigate } from "react-router-dom";
import Login from "./components/Login";
import Dashboard from "./components/Dashboard";
import ErrorStatusPage from "./pages/ErrorStatusPage";
import "./styles/layout.css";
import { getCurrentUser, logout as logoutApi } from "./services/authService";
import ProtectedRoute from "./components/routing/ProtectedRoute";
import { DEFAULT_PATH } from "./routes/routeConfig";
import { SESSION_EXPIRED_EVENT, clearAiSession } from "./services/aiServiceAuth";
import { toast } from "./utils/toast";

const DEV_BYPASS_AUTH = false;

function App() {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(DEV_BYPASS_AUTH);
  const [user, setUser] = useState(DEV_BYPASS_AUTH ? { name: "Admin" } : null);
  const [loading, setLoading] = useState(!DEV_BYPASS_AUTH); // Show loading while checking session
  const sessionEndingRef = useRef(false); // dedupe parallel 401s (e.g. polling)

  // Check session on app load using /auth/me endpoint
  useEffect(() => {
    if (DEV_BYPASS_AUTH) return;

    (async () => {
      try {
        const currentUser = await getCurrentUser();
        if (currentUser) {
          // Session is valid - store user info for UI display
          sessionStorage.setItem("auth", JSON.stringify({ user: currentUser }));
          setUser(currentUser);
          setIsAuthenticated(true);
        } else {
          // No valid session
          sessionStorage.removeItem("auth");
          setUser(null);
          setIsAuthenticated(false);
        }
      } catch (err) {
        console.warn("Session check failed:", err);
        sessionStorage.removeItem("auth");
        setUser(null);
        setIsAuthenticated(false);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    const handleHttpError = (event) => {
      const status = Number(event?.detail?.status);
      if (!Number.isInteger(status)) return;

      if (status === 401) {
        sessionStorage.removeItem("auth");
        setUser(null);
        setIsAuthenticated(false);
      }

      navigate(`/error/${status}`, {
        replace: true,
        state: {
          message: event?.detail?.message || "",
        },
      });
    };

    window.addEventListener("app:http-error", handleHttpError);
    return () => window.removeEventListener("app:http-error", handleHttpError);
  }, [navigate]);

  // onLogin expects { user } (no accessToken - it's in HttpOnly cookie now)
  const handleLogin = (authPayload, redirectPath = DEFAULT_PATH) => {
    const { user: nextUser } = authPayload || {};
    if (!nextUser) return;
    // Store user info for UI display only (token is in HttpOnly cookie)
    sessionStorage.setItem("auth", JSON.stringify({ user: nextUser }));
    sessionEndingRef.current = false;
    setIsAuthenticated(true);
    setUser(nextUser);
    if (redirectPath) {
      navigate(redirectPath, { replace: true });
    }
  };

  const handleLogout = useCallback(async () => {
    // Call logout endpoint to clear HttpOnly cookie
    await logoutApi();
    // Clear local session state
    sessionStorage.removeItem("auth");
    clearAiSession();
    try {
      localStorage.removeItem("auth");
    } catch {}
    setIsAuthenticated(false);
    setUser(null);
    navigate("/login", { replace: true });
  }, [navigate]);

  // AI SSO got a 401 (login expired, user logged out or disabled). There is no
  // refresh token, so end the session and send the user back to the login page.
  useEffect(() => {
    const handleSessionExpired = () => {
      if (sessionEndingRef.current) return;
      sessionEndingRef.current = true;
      toast.error("Your session has expired. Please log in again.");
      handleLogout();
    };
    window.addEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
    return () =>
      window.removeEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
  }, [handleLogout]);

  // Note: we intentionally do NOT clear on refresh; sessionStorage
  // is automatically cleared when the tab/window is closed.

  if (loading) {
    return (
      <div
        className="App"
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <div>Loading...</div>
      </div>
    );
  }

  const canAccess = DEV_BYPASS_AUTH || isAuthenticated;

  return (
    <div className="App">
      <Routes>
        <Route
          path="/error/:status"
          element={<ErrorStatusPage isAuthenticated={canAccess} />}
        />
        <Route
          path="/login"
          element={
            canAccess ?
              <Navigate to={DEFAULT_PATH} replace />
            : <Login onLogin={handleLogin} />
          }
        />
        <Route element={<ProtectedRoute isAuthenticated={canAccess} />}>
          <Route
            path="/*"
            element={<Dashboard user={user} onLogout={handleLogout} />}
          />
        </Route>
        <Route
          path="*"
          element={
            <Navigate to={canAccess ? DEFAULT_PATH : "/login"} replace />
          }
        />
      </Routes>
    </div>
  );
}

export default App;
